import { useMutation } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import { getCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { prepareGroupUser } from './group-user.prepare';
import type { BaseGroupUser, BaseGroupUserFull } from './types';

const order = {};
const groups = [];
const include = 'group';

/**
 * Fetch groups-users
 * @param params can be used to customise/limit the  groups-users retrieved
 * @returns the requested groups-users
 */
export const getGroupUsers: GetMany<BaseGroupUser> = async (params) =>
  (
    await ASSIST_API.get('groups-users', {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Fetch groups-users (singular)
 * @param params can be used to customise/limit the groups-users retrieved
 * @returns a single groups-users
 */
export const getGroupUser: GetOne<BaseGroupUser> = async (id, params) =>
  (
    await ASSIST_API.get(`groups-users/${id}`, {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Fetch groups-users with the related customer-user
 * @param params can be used to customise/limit the groups-users retrieved
 * @returns the requested groups-users and related customer-user
 */
export const getGroupUsersFull: GetMany<BaseGroupUserFull> = async (params) => {
  const users = await getGroupUsers(params);

  if (!users.meta?.totalItems) return users as RequestResponse<BaseGroupUser[]>;

  const customerUsers = await getCustomerUsers({
    filters: { id: users?.json.map(({ user }) => user.id) }
  });

  return {
    ...users,
    json: users.json.map((data) => ({
      ...data,
      user: {
        ...data.user,
        ...customerUsers.json.find((custUser) => custUser.id === data.user.id)
      }
    }))
  };
};

/**
 * Hook implementation to fetch groups-users
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns groups-users matching request
 */
export const useGroupUsers: AssistQuery.UseMany<BaseGroupUser> = (params, options) =>
  useQueryAssist(['groups-users', params], () => getGroupUsers(params), options);

/**
 * Given an group-user id fetch and return the area
 *  - only runs once group-user id is provided
 *  - fetches the one group-user
 *  - caches data
 * @param id group-user id
 * @param params optional parameters to change requested data
 * @returns the matching group-user
 */
export const useGroupUser: AssistQuery.UseOne<BaseGroupUser> = (id, params, options) =>
  useQueryAssist(['groups-users', { id, ...params }], () => getGroupUser(id, params), {
    ...options,
    enabled: !!id
  });

/**
 * Hook implementation to fetch groups-users with related customer-user
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns groups-users matching request
 */
export const useGroupUsersFull: AssistQuery.UseMany<BaseGroupUserFull> = (params, options) =>
  useQueryAssist(['groups-users', params], () => getGroupUsersFull(params), options);

/**
 * Creates methods to send DELETE requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteGroupUsers: AssistQuery.UseMutations<undefined, number[]> = (options) =>
  useMutation(
    (ids: number[]) => Promise.allSettled(ids.map((id) => ASSIST_API.delete(`groups-users/${id}`))),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['groups-users']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

type PostGroupUserVariables = {
  groupUser: RecursivePartial<BaseGroupUser>;
  classId: number;
  customerUserId: number;
};

/**
 * Creates methods to send POST requests to the api
 *
 * mutate call will require a customer ID
 * so the groupUser can be linked to the correct class
 *
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePostGroupUser: AssistQuery.UseMutation<BaseGroupUser, PostGroupUserVariables> = (
  options
) =>
  useMutation(
    ({ groupUser, classId, customerUserId }: PostGroupUserVariables) =>
      ASSIST_API.post('groups-users', prepareGroupUser(groupUser, classId, customerUserId), {
        params: requestParams({ order, groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['groups-users']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Given a customerUser and group fetch and return the existence of the relation
 *  - caches data
 * @returns the boolean indicator
 */
export const fetchGroupUserExists = (params, options): Promise<boolean> =>
  AssistQueryClient.fetchQuery(
    ['groups-users', { ...params }],
    async () =>
      !!(
        await ASSIST_API.get('groups-users', {
          params: requestParams(params)
        })
      ).data?.meta?.totalItems,
    options
  );
